import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Link, useSearchParams } from "react-router-dom";
import { GET_ALL_GISTS, GET_ALL_SUBJECTS } from "../../services/graphql/queriesMutations";
import { buildWorkspaceGistUrl } from "../../utils/publicUrls";
import styles from "./SubjectItemsPage.module.css";

type SubjectItem = {
  id: number;
  publicId?: string | null;
  title: string;
  parentId: number | null;
};

type GistComment = {
  id: number;
  comment?: string | null;
  isRemoved?: boolean | null;
};

type GistEdit = {
  id: number;
  body?: string | null;
  newnessCount?: number | null;
  importantCount?: number | null;
  qualityCount?: number | null;
  comments?: GistComment[] | null;
  createdAt?: string | null;
  user?: {
    id?: number | null;
    name?: string | null;
    image?: string | null;
  } | null;
};

type GistVersion = {
  id: number;
  point?: string | null;
  createdAt?: string | null;
  edits?: GistEdit[] | null;
  user?: {
    id?: number | null;
    name?: string | null;
  } | null;
};

type GistItem = {
  id: number;
  publicId?: string | null;
  title: string;
  parentId?: number | null;
  subjectId?: number | null;
  status?: string | null;
  publishedAt?: string | null;
  createdAt?: string | null;
  versions?: GistVersion[] | null;
};

type SubjectsQuery = {
  subjects: SubjectItem[];
};

type GistsQuery = {
  gists: GistItem[];
};

type FlatSubject = SubjectItem & {
  depth: number;
  childCount: number;
  descendantCount: number;
  directGistCount: number;
  branchGistCount: number;
};

type GistMetric = {
  commentCount: number;
  editCount: number;
  keyPointCount: number;
  latestActivity: string | null;
};

const DIRECT_LIMIT = 8;
const GIST_LIMIT = 12;

function buildSubjectMap(subjects: SubjectItem[]): Record<number, SubjectItem> {
  return subjects.reduce<Record<number, SubjectItem>>((acc, subject) => {
    acc[subject.id] = subject;
    return acc;
  }, {});
}

function sortByTitle<T extends { title: string }>(items: T[]): T[] {
  return items.slice().sort((a, b) => a.title.localeCompare(b.title));
}

function buildBuckets(subjects: SubjectItem[]): Record<string, SubjectItem[]> {
  const byId = buildSubjectMap(subjects);
  const buckets: Record<string, SubjectItem[]> = {};

  subjects.forEach((subject) => {
    const parentExists = typeof subject.parentId === "number" && Boolean(byId[subject.parentId]);
    const key = parentExists ? String(subject.parentId) : "root";
    buckets[key] = buckets[key] || [];
    buckets[key].push(subject);
  });

  Object.keys(buckets).forEach((key) => {
    buckets[key] = sortByTitle(buckets[key]);
  });

  return buckets;
}

function getDescendantIds(subjectId: number, buckets: Record<string, SubjectItem[]>): number[] {
  const result: number[] = [];
  const visit = (id: number) => {
    (buckets[String(id)] || []).forEach((child) => {
      result.push(child.id);
      visit(child.id);
    });
  };
  visit(subjectId);
  return result;
}

function getSubjectPath(subjects: SubjectItem[], subjectId: number | null): SubjectItem[] {
  if (subjectId === null) return [];
  const byId = buildSubjectMap(subjects);
  const path: SubjectItem[] = [];
  const visited = new Set<number>();
  let current: SubjectItem | undefined = byId[subjectId];

  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    path.unshift(current);
    current = typeof current.parentId === "number" ? byId[current.parentId] : undefined;
  }

  return path;
}

function getFirstMeaningfulText(gist: GistItem): string {
  const point = gist.versions?.find((version) => Boolean(version.point?.trim()))?.point;
  if (point) return point;

  const body = gist.versions
    ?.flatMap((version) => version.edits || [])
    .find((edit) => Boolean(edit.body?.trim()))?.body;

  return body || "Open this gist to read the current text and discussion.";
}

function stripHtml(value: string): string {
  return value
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function makeExcerpt(value: string, maxLength = 180): string {
  const text = stripHtml(value);
  if (!text) return "Open this gist to read the current text and discussion.";
  return text.length > maxLength ? `${text.slice(0, maxLength).trim()}…` : text;
}

function formatDate(value?: string | null): string {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No date";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function countGistMetrics(gist: GistItem): GistMetric {
  const versions = gist.versions || [];
  const edits = versions.flatMap((version) => version.edits || []);
  const comments = edits.flatMap((edit) => edit.comments || []).filter((comment) => !comment.isRemoved);
  const latestCandidates = [
    gist.createdAt,
    gist.publishedAt,
    ...versions.map((version) => version.createdAt),
    ...edits.map((edit) => edit.createdAt),
  ].filter(Boolean) as string[];

  const latestActivity = latestCandidates
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => b.getTime() - a.getTime())[0]?.toISOString() || null;

  return {
    commentCount: comments.length,
    editCount: edits.length,
    keyPointCount: versions.filter((version) => Boolean(version.point?.trim())).length,
    latestActivity,
  };
}

function getGistUrl(gist: GistItem, _subjectTitle: string): string {
  return buildWorkspaceGistUrl(gist, "read");
}


function sortGistsByActivity(items: GistItem[]): GistItem[] {
  return items.slice().sort((a, b) => {
    const aMetric = countGistMetrics(a).latestActivity || a.createdAt || "";
    const bMetric = countGistMetrics(b).latestActivity || b.createdAt || "";
    return bMetric.localeCompare(aMetric);
  });
}

function buildFlatSubjects(subjects: SubjectItem[], gists: GistItem[]): FlatSubject[] {
  const buckets = buildBuckets(subjects);
  const directGistCounts = gists.reduce<Record<number, number>>((acc, gist) => {
    if (typeof gist.subjectId !== "number") return acc;
    acc[gist.subjectId] = (acc[gist.subjectId] || 0) + 1;
    return acc;
  }, {});

  const result: FlatSubject[] = [];
  const visit = (subject: SubjectItem, depth: number) => {
    const descendants = getDescendantIds(subject.id, buckets);
    const branchSubjectIds = new Set([subject.id, ...descendants]);
    const branchGistCount = gists.filter((gist) => typeof gist.subjectId === "number" && branchSubjectIds.has(gist.subjectId)).length;

    result.push({
      ...subject,
      depth,
      childCount: (buckets[String(subject.id)] || []).length,
      descendantCount: descendants.length,
      directGistCount: directGistCounts[subject.id] || 0,
      branchGistCount,
    });

    (buckets[String(subject.id)] || []).forEach((child) => visit(child, depth + 1));
  };

  (buckets.root || []).forEach((subject) => visit(subject, 0));
  return result;
}

const SubjectItemsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSubjectId = Number(searchParams.get("subjectId"));
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(Number.isFinite(initialSubjectId) ? initialSubjectId : null);
  const [showBranchGists, setShowBranchGists] = useState(false);

  const { data: subjectData, loading: subjectsLoading, error: subjectsError } = useQuery<SubjectsQuery>(GET_ALL_SUBJECTS, {
    fetchPolicy: "cache-and-network",
  });
  const { data: gistData, loading: gistsLoading, error: gistsError } = useQuery<GistsQuery>(GET_ALL_GISTS, {
    fetchPolicy: "cache-and-network",
  });

  const subjects = useMemo(() => subjectData?.subjects || [], [subjectData]);
  const gists = useMemo(() => gistData?.gists || [], [gistData]);
  const buckets = useMemo(() => buildBuckets(subjects), [subjects]);
  const flatSubjects = useMemo(() => buildFlatSubjects(subjects, gists), [subjects, gists]);
  const selectedSubject = useMemo(
    () => subjects.find((subject) => subject.id === selectedSubjectId) || null,
    [subjects, selectedSubjectId],
  );
  const selectedPath = useMemo(() => getSubjectPath(subjects, selectedSubjectId), [subjects, selectedSubjectId]);

  useEffect(() => {
    if (selectedSubjectId === null) return;
    if (!subjects.length) return;
    if (!subjects.some((subject) => subject.id === selectedSubjectId)) {
      setSelectedSubjectId(null);
      setSearchParams({}, { replace: true });
    }
  }, [selectedSubjectId, setSearchParams, subjects]);

  const selectedDescendantIds = useMemo(
    () => (selectedSubject ? getDescendantIds(selectedSubject.id, buckets) : []),
    [buckets, selectedSubject],
  );

  const branchSubjectIds = useMemo(() => {
    if (!selectedSubject) return new Set<number>();
    return new Set([selectedSubject.id, ...selectedDescendantIds]);
  }, [selectedDescendantIds, selectedSubject]);

  const branchGists = useMemo(() => {
    if (!selectedSubject) return [];
    return sortGistsByActivity(
      gists.filter((gist) => typeof gist.subjectId === "number" && branchSubjectIds.has(gist.subjectId)),
    );
  }, [branchSubjectIds, gists, selectedSubject]);

  const directGists = useMemo(() => {
    if (!selectedSubject) return [];
    return sortGistsByActivity(gists.filter((gist) => gist.subjectId === selectedSubject.id));
  }, [gists, selectedSubject]);

  const visibleGists = showBranchGists ? branchGists : directGists;

  const selectedChildren = useMemo(
    () => (selectedSubject ? buckets[String(selectedSubject.id)] || [] : []),
    [buckets, selectedSubject],
  );

  const visibleSubjects = useMemo(() => {
    const subjectsWithContent = flatSubjects.filter((subject) => subject.branchGistCount > 0 || subject.childCount > 0);
    return subjectsWithContent.length ? subjectsWithContent : flatSubjects;
  }, [flatSubjects]);

  const loading = subjectsLoading || gistsLoading;
  const error = subjectsError || gistsError;

  const chooseSubject = (subjectId: number) => {
    setSelectedSubjectId(subjectId);
    setShowBranchGists(false);
    setSearchParams({ subjectId: String(subjectId) });
  };

  const clearSelection = () => {
    setSelectedSubjectId(null);
    setSearchParams({});
  };

  const selectedMetric = useMemo(() => {
    const commentCount = branchGists.reduce((total, gist) => total + countGistMetrics(gist).commentCount, 0);
    const editCount = branchGists.reduce((total, gist) => total + countGistMetrics(gist).editCount, 0);
    return {
      directSubjects: selectedChildren.length,
      descendantSubjects: selectedDescendantIds.length,
      directGists: directGists.length,
      branchGists: branchGists.length,
      gists: visibleGists.length,
      comments: commentCount,
      edits: editCount,
    };
  }, [branchGists, directGists.length, selectedChildren.length, selectedDescendantIds.length, visibleGists.length]);

  return (
    <main className={styles.page}>
      <div className={styles.shell}>
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <Link to="/">Subjects</Link>
          {selectedPath.map((subject) => (
            <React.Fragment key={subject.id}>
              <span>›</span>
              <button type="button" onClick={() => chooseSubject(subject.id)}>
                {subject.title}
              </button>
            </React.Fragment>
          ))}
        </nav>

        <header className={styles.headerPanel}>
          <div>
            <span className={styles.eyebrow}>Live Subject Items</span>
            <h1>{selectedSubject ? selectedSubject.title : "Choose a subject"}</h1>
            <p>
              This page uses the Subject Items design with real Gistoscope data. Select a subject to see the actual child
              subjects and gists stored in your database.
            </p>
          </div>
          <div className={styles.headerStats} aria-label="Live database counts">
            <strong>{subjects.length}</strong>
            <span>real subjects</span>
            <strong>{gists.length}</strong>
            <span>real gists</span>
          </div>
        </header>

        {loading && <div className={styles.statePanel}>Loading real subjects and gists…</div>}
        {error && <div className={styles.errorPanel}>Unable to load live data: {error.message}</div>}

        {!loading && !error && (
          <div className={styles.layout}>
            <aside className={styles.subjectRail} aria-label="Subjects">
              <div className={styles.railHeader}>
                <div>
                  <h2>Subjects</h2>
                  <p>Pick a real subject from the database.</p>
                </div>
                {selectedSubject && (
                  <button type="button" onClick={clearSelection}>
                    Clear
                  </button>
                )}
              </div>

              <div className={styles.subjectList}>
                {visibleSubjects.map((subject) => {
                  const active = selectedSubjectId === subject.id;
                  return (
                    <button
                      type="button"
                      key={subject.id}
                      className={`${styles.subjectListItem} ${active ? styles.subjectListItemActive : ""}`}
                      style={{ paddingLeft: `${16 + Math.min(subject.depth, 4) * 16}px` }}
                      onClick={() => chooseSubject(subject.id)}
                    >
                      <span className={styles.subjectDot} aria-hidden="true" />
                      <span>
                        <strong>{subject.title}</strong>
                        <small>
                          {subject.branchGistCount} gists · {subject.descendantCount} child subjects
                        </small>
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>

            <section className={styles.contentColumn}>
              {!selectedSubject ? (
                <section className={styles.emptySelectionPanel}>
                  <div className={styles.emptyIcon} aria-hidden="true">◒</div>
                  <h2>Start with the subject list</h2>
                  <p>
                    This view intentionally starts with real subjects only. Gists appear after you choose a subject, so the
                    difference between a subject and a gist is visually clear.
                  </p>
                  <div className={styles.quickGrid}>
                    {visibleSubjects.slice(0, DIRECT_LIMIT).map((subject) => (
                      <button type="button" key={subject.id} onClick={() => chooseSubject(subject.id)}>
                        <strong>{subject.title}</strong>
                        <span>{subject.branchGistCount} gists in branch</span>
                      </button>
                    ))}
                  </div>
                </section>
              ) : (
                <>
                  <section className={styles.subjectPanel} aria-labelledby="subject-items-selected-title">
                    <div className={styles.subjectBadge}>Subject</div>
                    <div className={styles.subjectIcon} aria-hidden="true">
                      <span>◒</span>
                    </div>
                    <div className={styles.subjectCopy}>
                      <h2 id="subject-items-selected-title">{selectedSubject.title}</h2>
                      <p>
                        This is a real subject from your database. By default, the cards below show only direct gists attached
                        to this exact subject; child-subject gists stay inside their own folders.
                      </p>
                      <div className={styles.statRow}>
                        <span>{selectedMetric.directGists} direct gists</span>
                        <span>{selectedMetric.branchGists} gists in branch</span>
                        <span>{selectedMetric.directSubjects} direct child subjects</span>
                        <span>{selectedMetric.descendantSubjects} descendant subjects</span>
                        <span>{selectedMetric.comments} visible comments</span>
                        <span>{selectedMetric.edits} edits</span>
                      </div>
                    </div>
                    <Link className={styles.openSubjectLink} to={`/subject/${encodeURIComponent(selectedSubject.title)}/${selectedSubject.id}`}>
                      Open subject page
                    </Link>
                  </section>

                  <div className={styles.parentConnector} aria-hidden="true">
                    <span />
                    <strong>Gists and child subjects</strong>
                    <span />
                  </div>

                  {selectedChildren.length > 0 && (
                    <section className={styles.childSubjectPanel} aria-labelledby="child-subjects-title">
                      <header>
                        <h2 id="child-subjects-title">Child subjects</h2>
                        <p>Move deeper into the real subject tree.</p>
                      </header>
                      <div className={styles.childSubjectGrid}>
                        {selectedChildren.slice(0, DIRECT_LIMIT).map((child) => {
                          const childFlat = flatSubjects.find((subject) => subject.id === child.id);
                          return (
                            <button type="button" key={child.id} onClick={() => chooseSubject(child.id)}>
                              <strong>{child.title}</strong>
                              <span>{childFlat?.branchGistCount || 0} gists · {childFlat?.descendantCount || 0} descendants</span>
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  )}

                  <section className={styles.gistsPanel} aria-labelledby="real-gists-title">
                    <header className={styles.sectionHeaderInline}>
                      <div>
                        <h2 id="real-gists-title">{showBranchGists ? "All gists in this branch" : "Direct gists in this subject"}</h2>
                        <p>
                          {showBranchGists
                            ? "Branch mode is intentionally explicit: it includes descendant-subject gists."
                            : "Direct mode keeps parent subjects clean and shows only gists attached here."}
                        </p>
                      </div>
                      <div className={styles.sectionActions}>
                        {branchGists.length !== directGists.length && (
                          <button type="button" onClick={() => setShowBranchGists((current) => !current)}>
                            {showBranchGists ? "Show direct only" : `Show all ${branchGists.length} branch gists`}
                          </button>
                        )}
                        <Link to={`/gist-lab?subjectId=${selectedSubject.id}`}>Open Gist lab →</Link>
                      </div>
                    </header>

                    {visibleGists.length === 0 ? (
                      <div className={styles.emptyGists}>
                        {showBranchGists
                          ? "No gists are connected to this subject branch yet."
                          : "No direct gists are attached to this subject. Open a child subject or switch to branch mode."}
                      </div>
                    ) : (
                      <div className={styles.gistGrid}>
                        {visibleGists.slice(0, GIST_LIMIT).map((gist, index) => {
                          const metrics = countGistMetrics(gist);
                          const subjectTitle = subjects.find((subject) => subject.id === gist.subjectId)?.title || selectedSubject.title;
                          const tone = index % 4;
                          return (
                            <article key={gist.id} className={`${styles.gistCard} ${styles[`gistTone${tone}`]}`}>
                              <div className={styles.gistTopline}>
                                <span aria-hidden="true">{gist.parentId ? "↳" : "⚑"}</span>
                                <span>{gist.parentId ? "Reply gist" : "Root gist"}</span>
                                {gist.status && <em>{gist.status}</em>}
                              </div>
                              <h3>{gist.title}</h3>
                              <p>{makeExcerpt(getFirstMeaningfulText(gist))}</p>
                              <footer className={styles.gistFooter}>
                                <span>{metrics.commentCount} comments</span>
                                <span>{metrics.keyPointCount} key points</span>
                                <span>{formatDate(metrics.latestActivity || gist.createdAt)}</span>
                              </footer>
                              <Link className={styles.gistLink} to={getGistUrl(gist, subjectTitle)}>
                                Open gist →
                              </Link>
                            </article>
                          );
                        })}
                      </div>
                    )}
                  </section>
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </main>
  );
};

export default SubjectItemsPage;
